import React from 'react'
import { Table, Button, Tag, Space, Dropdown, Progress } from 'antd'
import { MoreOutlined, PlusCircleOutlined, StopOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { Trial } from '../types/trial.types'

interface TrialsTableProps {
    trials: Trial[]
    onExtend: (trial: Trial) => void
    onRevoke: (trial: Trial) => void
}

export const TrialsTable: React.FC<TrialsTableProps> = ({
    trials,
    onExtend,
    onRevoke
}) => {
    const columns: ColumnsType<Trial> = [
        {
            title: 'Merchant',
            dataIndex: 'merchantName',
            key: 'merchantName',
            render: (name: string, record) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{name}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{record.merchantEmail}</div>
                </div>
            ),
        },
        {
            title: 'Duration',
            key: 'duration',
            render: (_, record) => {
                const percent = record.status === 'active' && record.remainingDays !== undefined
                    ? Math.round(((record.duration - record.remainingDays) / record.duration) * 100)
                    : 100

                return (
                    <div style={{ width: 150 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span>{record.duration} Days</span>
                            {record.status === 'active' && (
                                <span style={{ color: percent > 80 ? 'red' : 'inherit' }}>
                                    {record.remainingDays} left
                                </span>
                            )}
                        </div>
                        <Progress
                            percent={percent}
                            size="small"
                            status={record.status === 'active' ? 'active' : (record.status === 'expired' ? 'exception' : 'normal')}
                            showInfo={false}
                        />
                    </div>
                )
            }
        },
        {
            title: 'Radius (B/V)',
            key: 'radius',
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Tag color="blue">Bidding: {record.trialBiddingRadiusKm} KM</Tag>
                    <Tag color="cyan">Visible: {record.trialVisibilityRadiusKm} KM</Tag>
                </Space>
            ),
        },
        {
            title: 'Start Date',
            dataIndex: 'startDate',
            key: 'startDate',
        },
        {
            title: 'End Date',
            dataIndex: 'endDate',
            key: 'endDate',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const colors = {
                    active: 'green',
                    expired: 'orange',
                    revoked: 'red',
                }
                return <Tag color={colors[status as keyof typeof colors]}>{status.toUpperCase()}</Tag>
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Dropdown
                    menu={{
                        items: [
                            {
                                key: 'extend',
                                icon: <PlusCircleOutlined />,
                                label: 'Extend Trial',
                                disabled: record.status !== 'active' && record.status !== 'expired',
                                onClick: () => onExtend(record),
                            },
                            {
                                key: 'revoke',
                                icon: <StopOutlined />,
                                label: 'Revoke Access',
                                danger: true,
                                disabled: record.status !== 'active',
                                onClick: () => onRevoke(record),
                            },
                        ],
                    }}
                >
                    <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
            ),
        },
    ]

    return (
        <Table
            columns={columns}
            dataSource={trials}
            rowKey="id"
            pagination={{
                total: trials.length,
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} trials`,
            }}
        />
    )
}
